import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { emailText, normalizeSessions, otherSessionsText, SECURITY_CHOICES } from "@/lib/v3Settings";
import { shortName } from "@/lib/v3People";
import { useMemberAuth } from "../MemberAuthContext";
import { useMemberProfile } from "../useMemberProfile";
import { useCore } from "../useCore";
import { ConnectionError } from "../components/ConnectionError";
import { Loading, ConfirmDialog } from "../components/kit";
import { V3Title, V3Columns, V3Actions, SettingRow, V3Switch, StatusBanner, DetailRow, DetailList, V3_PRIMARY, V3_SECONDARY } from "../components/v3";
import { AreaCard, ResponsibleCard } from "../components/v3app";
import { CheckRow, CheckList, Blocked, FieldError } from "../components/v3plans";
import { SettingsAside } from "./Settings";

// Loads the own profile once and hands it to the view; Core errors show M26.
function WithProfile({ title, intro, testid, children }) {
  const p = useMemberProfile();
  return (
    <div data-testid={testid}>
      <V3Title title={title} intro={intro} testid={`${testid}-title`} />
      {p.error ? <ConnectionError onRetry={p.load} /> : !p.data ? <Loading /> : children(p)}
    </div>
  );
}

const LOCKED = "Einzeln noch nicht verfügbar";

// M115: only the overall in-app switch (prefs.notifications) is stored by the Core; kinds, e-mail and quiet hours need D-98.
function NotifyForm({ p }) {
  const saved = p.prefs.notifications !== false;
  const [on, setOn] = useState(saved);
  const [note, setNote] = useState("");
  const save = async () => {
    if (on === saved) { setNote("Es gibt keine Änderung – es wurde nichts gesendet."); return; }
    await p.savePrefs({ ...p.prefs, notifications: on }, "Benachrichtigungen gespeichert.");
  };
  return (
    <V3Columns layout="settings" aside={<SettingsAside a11y={p.a11y} />} main={<div data-m="M115">
      <div className="space-y-[12px]">
        <SettingRow title="Mitteilungen in der App" value={on ? "Ein · Hinweise zu Kontakten, Aktivitäten und Nachrichten" : "Aus"} testid="notify-app">
          <V3Switch checked={on} onChange={(v) => { setOn(v); setNote(""); }} disabled={p.saving} label="Mitteilungen in der App" testid="member-notifications" />
        </SettingRow>
        <SettingRow title="Teilnahme und Terminänderung" value={on ? "In-App anzeigen" : "Aus"} note={LOCKED} testid="notify-participation">
          <V3Switch checked={on} disabled onChange={() => {}} label="Teilnahme und Terminänderung einzeln (noch nicht verfügbar)" describedBy="notify-participation-note notify-unavailable" testid="notify-participation-switch" />
        </SettingRow>
        <SettingRow title="Nachrichten" value="Vorschau ohne private Inhalte" testid="notify-messages" />
        <SettingRow title="E-Mail-Hinweise" value="Bewusst auswählen" note="Noch nicht verfügbar" testid="notify-email">
          <V3Switch checked={false} disabled onChange={() => {}} label="E-Mail-Hinweise (noch nicht verfügbar)" describedBy="notify-email-note notify-unavailable" testid="notify-email-switch" />
        </SettingRow>
        <SettingRow title="Ruhezeiten" value="Nicht festgelegt" note="Noch nicht verfügbar" testid="notify-quiet">
          <V3Switch checked={false} disabled onChange={() => {}} label="Ruhezeiten (noch nicht verfügbar)" describedBy="notify-quiet-note notify-unavailable" testid="notify-quiet-switch" />
        </SettingRow>
      </div>
      <Blocked id="notify-unavailable">Einzelne Arten, E-Mail-Hinweise und Ruhezeiten kann der gemeinsame Core noch nicht speichern. Gespeichert wird nur „Mitteilungen in der App“.</Blocked>
      {note && <p role="status" data-testid="notify-note" className="mt-[12px] text-[13px] font-bold text-v3-muted">{note}</p>}
      <V3Actions>
        <button type="button" onClick={save} disabled={p.saving} data-testid="notify-save" className={V3_PRIMARY}>{p.saving ? "Wird gespeichert …" : "Einstellungen speichern"}</button>
        <Link to="/einstellungen" data-testid="notify-back" className={V3_SECONDARY}>Zurück</Link>
      </V3Actions>
    </div>} />
  );
}

export const NotificationSettings = () => (
  <WithProfile title="Benachrichtigungseinstellungen" intro="Wähle Benachrichtigungsarten und Kanäle einzeln aus." testid="notify-page">{(p) => <NotifyForm p={p} />}</WithProfile>
);

const SECURITY_BLOCKED = {
  email: "Die E-Mail-Adresse kann in der App noch nicht geändert werden. Es wird nichts gesendet.",
  password: "Das Passwort kann in der App noch nicht mit Identitätsprüfung geändert werden. Es wird nichts gesendet.",
};

// M100: choose one row; e-mail and password stay locked (D-96), the current session can really end, other sessions open M111.
function SecurityForm({ p }) {
  const navigate = useNavigate();
  const { logout } = useMemberAuth();
  const sessions = useCore("/member/sessions", (d) => ({ list: normalizeSessions(d) }));
  const list = sessions.error === "notfound" ? null : sessions.data ? sessions.data.list : sessions.error ? null : undefined;
  const [choice, setChoice] = useState("");
  const [error, setError] = useState("");
  const [blocked, setBlocked] = useState("");
  const [ask, setAsk] = useState(false);
  const mail = emailText(p.data.profile);
  const pick = (v) => () => { setChoice(v); setError(""); setBlocked(""); };
  const radio = (v) => ({ type: "radio", name: "security-choice", value: v, onChange: pick(v), "aria-describedby": error ? "security-choice-error" : undefined });
  const go = () => {
    if (!SECURITY_CHOICES.includes(choice)) { setError("Bitte wähle eine Einstellung aus."); return; }
    if (choice === "current") setAsk(true);
    else if (choice === "others") navigate("/einstellungen/sitzungen");
    else setBlocked(SECURITY_BLOCKED[choice]);
  };
  return (
    <V3Columns layout="detail" aside={<><AreaCard text={mail} testid="security-area" /><ResponsibleCard /></>} main={<div data-m="M100">
      <CheckList>
        <div role="radiogroup" aria-label="Sicherheitseinstellung" className="space-y-[13px]">
          <CheckRow title="E-Mail" value={mail} checked={choice === "email"} input={radio("email")} testid="security-email" />
          <CheckRow title="Passwort" value="Mit Identitätsprüfung ändern" checked={choice === "password"} input={radio("password")} testid="security-password" />
          <CheckRow title="Aktuelle Sitzung" value="Dieses Gerät · hier abmelden" checked={choice === "current"} input={radio("current")} testid="security-current" />
          <CheckRow title="Weitere Sitzungen" value={list === undefined ? "Wird geladen …" : otherSessionsText(list)} checked={choice === "others"} input={radio("others")} testid="security-others" />
        </div>
      </CheckList>
      <FieldError id="security-choice-error">{error}</FieldError>
      {blocked && <Blocked id="security-blocked">{blocked}</Blocked>}
      <V3Actions>
        <button type="button" onClick={go} data-testid="security-choose" className={V3_PRIMARY}>Sicherheitseinstellung wählen</button>
        <Link to="/einstellungen" data-testid="security-back" className={V3_SECONDARY}>Zurück</Link>
      </V3Actions>
      <ConfirmDialog open={ask} onOpenChange={setAsk} testid="security-logout-dialog" title="Auf diesem Gerät abmelden?"
        description="Du wirst nur auf diesem Gerät abgemeldet. Danach meldest du dich mit E-Mail und Passwort erneut an." confirmLabel="Abmelden"
        onConfirm={async () => { setAsk(false); await logout(); }} />
    </div>} />
  );
}

export const AccountSecurity = () => (
  <WithProfile title="Kontosicherheit verwalten" intro="Verwalte die Sicherheit deines eigenen Mitgliederkontos." testid="security-page">{(p) => <SecurityForm p={p} />}</WithProfile>
);

// M111: ending the other sessions needs POST /member/sessions/revoke-others (D-97); nothing is ended.
function SignOutOthers() {
  const { member } = useMemberAuth();
  const sessions = useCore("/member/sessions", (d) => ({ list: normalizeSessions(d) }));
  const list = sessions.data ? sessions.data.list : null;
  const others = Array.isArray(list) ? otherSessionsText(list) : "Anzahl noch nicht verfügbar";
  const name = shortName(member?.name) || "Dein Konto";
  return (
    <V3Columns layout="detail" aside={<><AreaCard title="Ausgewählter Vorgang" text={name} testid="sessions-area" /><ResponsibleCard /></>} main={<div data-m="M111">
      <StatusBanner tone="notice" title="Deine Bestätigung ist erforderlich" text="Prüfe den angezeigten Vorgang und die konkreten Auswirkungen." testid="sessions-confirm" />
      <DetailList>
        <DetailRow label="Konto" value={name} testid="sessions-account" />
        <DetailRow label="Dieses Gerät" value="Aktuelle Sitzung · bleibt angemeldet" testid="sessions-current" />
        <DetailRow label="Andere Geräte" value={`${others} · würden nach Bestätigung abgemeldet`} testid="sessions-others" />
        <DetailRow label="Erneuter Zugang" value="Persönliche Anmeldung erforderlich" testid="sessions-reentry" />
      </DetailList>
      <Blocked id="sessions-blocked">Andere Geräte abzumelden ist im gemeinsamen Core noch nicht eingerichtet. Es wird nichts beendet; deine anderen Sitzungen bleiben aktiv.</Blocked>
      <V3Actions>
        <button type="button" disabled aria-describedby="sessions-blocked" data-testid="sessions-signout" className={V3_PRIMARY}>Andere Geräte abmelden</button>
        <Link to="/einstellungen/sicherheit" data-testid="sessions-stay" className={V3_SECONDARY}>Angemeldet bleiben</Link>
      </V3Actions>
    </div>} />
  );
}

export function SessionsSignOut() {
  return (
    <div data-testid="sessions-page">
      <V3Title title="Alle Geräte abmelden" intro="Melde deine eigenen Sitzungen auf den anderen Geräten ab." testid="sessions-page-title" />
      <SignOutOthers />
    </div>
  );
}
